// ExpandedPanel.js — the full-height command center. Header pinned top,
// chat bar pinned bottom, body between: session rail → action queue /
// patient view (or review mode).
//
// WIDE layouts (the web app; not the 350px extension side panel) split
// the body into two columns: queue on the left, PatientPanel on the
// right. NARROW layouts show the same two views as bottom tabs (QUEUE /
// PATIENT) — the timeline stays up in both. Timeline and chat are always
// full width. The patient view auto-follows the session — recording now,
// else next up — until a tap on a card/nudge pins a specific patient.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useComprehendLive } from 'agent/useComprehendLive';
import { QUEUE_KINDS } from 'agent/liveStore';
import { parseBackendDateMs } from 'agent/time';
import SessionRail from './SessionRail';
import ActionQueue from './ActionQueue';
import ReviewMode from './ReviewMode';
import PatientPanel from './PatientPanel';
import ChatBar from './ChatBar';
import ChatOverlay from './ChatOverlay';
import SettingsSheet from './SettingsSheet';
import BrainMonitor from './BrainMonitor';
import ViewToggle from './ViewToggle';
import MiniSessionBanner from './MiniSessionBanner';
import { T, agentMode } from './liveTheme';

// Split only when BOTH columns get roughly the 350px the content was
// designed for — the extension side panel (≤350) always stays stacked.
const WIDE_MIN = 660;

const ExpandedPanel = () => {
  const { state, actions, apiClient } = useComprehendLive();
  const bodyRef = useRef(null);
  const rootRef = useRef(null);
  const [wide, setWide] = useState(false);
  // Narrow layout: the body is tabbed — QUEUE or PATIENT — switched at
  // the bottom (thumb territory in the side panel). A tap on any card or
  // nudge jumps to the patient tab pinned to that patient.
  const [narrowTab, setNarrowTab] = useState('queue');
  // Internal X-ray of the ambient pipeline (dev only) — 🧠 header chip.
  const [brainOpen, setBrainOpen] = useState(false);
  useEffect(() => {
    if (state.drillInConversationId) setNarrowTab('patient');
  }, [state.drillInConversationId]);

  useEffect(() => {
    const el = rootRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    // Defer the width read to the next frame so the rapid resizes during the
    // expand animation don't trip "ResizeObserver loop ...".
    let raf = null;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect?.width || 0;
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => setWide(w >= WIDE_MIN));
    });
    ro.observe(el);
    return () => {
      if (raf) cancelAnimationFrame(raf);
      ro.disconnect();
    };
  }, []);

  // Who the patient column shows: an explicit tap pins; otherwise follow
  // the session — recording now (latest started), else next scheduled,
  // else the most recent visit today.
  const focusConversationId = useMemo(() => {
    if (state.drillInConversationId) return state.drillInConversationId;
    const today = state.sessions.filter((s) => s.isToday !== false);
    const byStart = (a, b) =>
      (parseBackendDateMs(a.createdAtMs ?? a.createdAt) || 0) -
      (parseBackendDateMs(b.createdAtMs ?? b.createdAt) || 0);
    const recording = today.filter((s) => s.recording).sort(byStart);
    if (recording.length) return recording[recording.length - 1].conversationId;
    const now = Date.now();
    const upcoming = today
      .filter((s) => parseBackendDateMs(s.createdAtMs ?? s.createdAt) > now)
      .sort(byStart)[0];
    if (upcoming) return upcoming.conversationId;
    const recent = [...today].sort(byStart).pop();
    return recent ? recent.conversationId : null;
  }, [state.drillInConversationId, state.sessions]);

  // Concurrent treatment (2:1 / 3:1): everyone recording right now. Shown
  // as a switcher strip in the patient column — full panels stacked would
  // be ~3 screens of scrolling exactly when the provider has the least
  // time; chips give one-glance triage (who's live, who has nudges) and
  // one tap flips the panel.
  const concurrent = useMemo(
    () =>
      state.sessions
        .filter((s) => s.recording)
        .sort(
          (a, b) =>
            (parseBackendDateMs(a.createdAtMs ?? a.createdAt) || 0) -
            (parseBackendDateMs(b.createdAtMs ?? b.createdAt) || 0),
        ),
    [state.sessions],
  );
  const nudgeCountFor = (conversationId) =>
    state.queue.filter(
      (q) =>
        q.kind === QUEUE_KINDS.NUDGE &&
        q.conversationId === conversationId &&
        q.status === 'pending',
    ).length;

  const lastChat = [...state.chat].reverse().find((m) => m.role !== 'step');
  // The ghost trace of the last message reopens the conversation on TAP.
  // (Wheel/overscroll gestures were tried twice and cut — trackpad
  // momentum makes them fire unpredictably.)
  const showGhost = !state.chatOverlay && Boolean(lastChat);

  // ONE patient view, two homes: the wide layout's right column and the
  // narrow layout's PATIENT tab render the same thing — concurrent
  // switcher chips (2:1/3:1) over the followed/pinned patient.
  const patientColumn = (
    <div style={{ paddingTop: 12 }}>
      {concurrent.length > 1 && (
        <div
          style={{
            display: 'flex',
            gap: 6,
            flexWrap: 'wrap',
            marginBottom: 10,
          }}
        >
          {concurrent.map((s) => {
            const active = s.conversationId === focusConversationId;
            const nudges = nudgeCountFor(s.conversationId);
            return (
              <span
                key={s.conversationId}
                onClick={() => actions.openDrillIn(s.conversationId)}
                role="button"
                title={s.patientName || s.name}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 5,
                  padding: '3px 9px',
                  borderRadius: 10,
                  fontSize: 11,
                  fontWeight: 600,
                  cursor: 'pointer',
                  background: active ? T.surfaceCard : 'transparent',
                  color: active ? T.textPrimary : T.textSecondary,
                  border: `1px solid ${
                    active ? 'rgba(107,216,136,0.45)' : T.border
                  }`,
                }}
              >
                <span
                  className="cpl-dot"
                  style={{ background: T.red, width: 6, height: 6 }}
                />
                {(s.patientName || s.name).split(' ')[0]}
                {nudges > 0 && (
                  <span
                    style={{
                      background: 'rgba(251,191,36,0.16)',
                      color: T.amber,
                      fontSize: 9,
                      fontWeight: 700,
                      padding: '0 5px',
                      borderRadius: 7,
                    }}
                  >
                    {nudges}
                  </span>
                )}
              </span>
            );
          })}
        </div>
      )}
      <PatientPanel
        conversationId={focusConversationId}
        pinned={Boolean(state.drillInConversationId)}
        onUnpin={actions.closeDrillIn}
      />
    </div>
  );

  let body;
  const mainView = !state.reviewMode;
  if (state.reviewMode) body = <ReviewMode />;
  else if (!wide && narrowTab === 'patient') body = patientColumn;
  else body = <ActionQueue />;
  const splitView = wide && mainView;

  const focusSession = state.sessions.find(
    (s) => s.conversationId === focusConversationId,
  );
  const pendingCount = state.queue.filter((q) => q.status === 'pending').length;

  const ghostTrace = lastChat ? (
    <div
      onClick={actions.reopenChat}
      role="button"
      title="Reopen the conversation"
      style={{
        margin: '10px 0 8px',
        padding: '6px 10px',
        borderTop: `1px solid ${T.border}`,
        opacity: 0.65,
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'flex-start',
        gap: 7,
      }}
    >
      <i
        className={`fa ${
          lastChat.role === 'receipt' ? 'fa-check' : 'fa-comment'
        }`}
        aria-hidden="true"
        style={{ color: T.textFaint, fontSize: 10, marginTop: 2 }}
      />
      <p
        style={{
          margin: 0,
          flex: 1,
          minWidth: 0,
          fontSize: 10,
          fontStyle: 'italic',
          color: T.textFaint,
          lineHeight: 1.4,
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
        }}
      >
        {lastChat.text}
      </p>
      <i
        className="fa fa-chevron-up"
        aria-hidden="true"
        style={{ color: T.textFaint, fontSize: 9, marginTop: 2 }}
      />
    </div>
  ) : null;

  return (
    <div
      ref={rootRef}
      style={{
        background: T.surface,
        flex: 1,
        minHeight: 0,
        // Flex items won't shrink below content min-width by default — the
        // timeline's growing total width was widening the whole panel and
        // pushing the chat send button off-screen. Pin it.
        minWidth: 0,
        maxWidth: '100%',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        animation: 'cplSlide .18s ease',
        boxShadow: '0 12px 24px rgba(13,35,30,0.45)',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 7,
          padding: '10px 12px 8px',
          flexShrink: 0,
        }}
      >
        <span
          className={agentMode(state).pulse ? 'cpl-dot' : undefined}
          style={{
            display: 'inline-block',
            width: agentMode(state).big ? 12 : 8,
            height: agentMode(state).big ? 12 : 8,
            borderRadius: '50%',
            background: agentMode(state).color,
            transition: 'all .2s ease',
          }}
        />
        <span
          style={{
            color: agentMode(state).color,
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: '.5px',
            flexShrink: 0,
          }}
        >
          {agentMode(state).key === 'live' ||
          agentMode(state).key === 'ptt' ||
          agentMode(state).key === 'standby'
            ? 'COMPREHEND LIVE'
            : agentMode(state).label}
        </span>
        <span
          style={{
            color: T.textFaint,
            fontSize: 10,
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {state.sessions.filter((s) => s.recording).length} recording ·{' '}
          {state.queue.filter((q) => q.status === 'pending').length} pending
        </span>
        {/* Ambient users get both experiences — flip to the timeline view. */}
        <span style={{ marginLeft: 'auto', flexShrink: 0 }}>
          <ViewToggle />
        </span>
        {/* Passive-listening opt-in (persisted, OFF by default). The gear
            menu carries the labelled version; this is the always-visible
            one-click switch, because a default-off control buried in a
            submenu is a control nobody finds. */}
        <span
          onClick={actions.toggleAutoListen}
          role="button"
          aria-pressed={state.autoListen}
          aria-label={
            state.autoListen ? 'Turn off listening' : 'Turn on listening'
          }
          title={
            state.autoListen
              ? 'Listening is ON — wake word + ambient capture live. Click to stop (remembered next load).'
              : 'Listening is OFF — nothing opens the mic. Click to enable "Hey Comprehend" + ambient capture.'
          }
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 22,
            height: 22,
            borderRadius: '50%',
            cursor: 'pointer',
            flexShrink: 0,
            background: state.autoListen
              ? 'rgba(107,216,136,0.18)'
              : 'transparent',
            border: `1px solid ${
              state.autoListen ? 'rgba(107,216,136,0.6)' : T.border
            }`,
            color: state.autoListen ? T.greenBright : T.textFaint,
          }}
        >
          <i
            className="fa fa-power-off"
            aria-hidden="true"
            style={{ fontSize: 10 }}
          />
        </span>
        {/* Hard mic kill: ambient + wake word + realtime session all
            release the mic (browser indicator goes dark). Typing still
            works; voice features return on un-kill. */}
        <span
          onClick={actions.toggleMicKilled}
          role="button"
          aria-label={
            state.micKilled ? 'Restore mic access' : 'Kill all mic access'
          }
          title={
            state.micKilled
              ? 'Mic is OFF — click to restore listening'
              : 'Kill the mic — stop ambient, wake word, and voice session'
          }
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 22,
            height: 22,
            borderRadius: '50%',
            cursor: 'pointer',
            flexShrink: 0,
            background: state.micKilled ? 'rgba(245,54,92,0.2)' : 'transparent',
            border: `1px solid ${
              state.micKilled ? 'rgba(245,54,92,0.6)' : T.border
            }`,
            color: state.micKilled ? T.redSoft : T.textFaint,
          }}
        >
          <i
            className={`fa ${
              state.micKilled ? 'fa-microphone-slash' : 'fa-microphone'
            }`}
            aria-hidden="true"
            style={{ fontSize: 10 }}
          />
        </span>
        {apiClient?.isDev && (
          <span
            onClick={() => setBrainOpen((v) => !v)}
            title="Brain monitor — watch the ambient pipeline"
            role="button"
            style={{
              fontSize: 11,
              cursor: 'pointer',
              flexShrink: 0,
              padding: '1px 5px',
              borderRadius: 8,
              border: `1px solid ${brainOpen ? T.greenBright : T.border}`,
              opacity: state.ambientStatus === 'listening' ? 1 : 0.55,
            }}
          >
            🧠
          </span>
        )}
        {apiClient?.isDev && (
          <span
            onClick={actions.toggleDemo}
            title={
              state.demo
                ? 'Exit demo mode (reloads)'
                : 'Start the 5-min scripted demo (reloads)'
            }
            style={{
              fontSize: 9,
              fontWeight: 700,
              letterSpacing: '.5px',
              padding: '2px 7px',
              borderRadius: 8,
              cursor: 'pointer',
              flexShrink: 0,
              background: state.demo ? T.amber : 'transparent',
              color: state.demo ? '#412402' : T.textFaint,
              border: `1px solid ${state.demo ? T.amber : T.border}`,
            }}
          >
            {state.demo ? 'DEMO ON' : 'DEMO'}
          </span>
        )}
        <i
          className="fa fa-chevron-up"
          aria-hidden="true"
          onClick={() => actions.setExpanded(false)}
          style={{
            color: T.textSecondary,
            fontSize: 12,
            cursor: 'pointer',
            padding: 4,
          }}
        />
      </div>

      {mainView &&
        // While the conversation layer is up the timeline gives way to the
        // minimal recording banner (same language as the collapsed rail);
        // the full timeline returns when the chat dismisses.
        (state.chatOverlay && state.chatOverlayMode === 'full' ? (
          <MiniSessionBanner />
        ) : (
          <div style={{ flexShrink: 0, minWidth: 0, padding: '0 12px' }}>
            <SessionRail />
          </div>
        ))}

      <div
        style={{
          position: 'relative',
          flex: 1,
          minHeight: 0,
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {splitView ? (
          <div style={{ flex: 1, minHeight: 0, minWidth: 0, display: 'flex' }}>
            <div
              ref={bodyRef}
              className="cpl-scroll"
              style={{
                flex: 1,
                minWidth: 0,
                overflowY: 'auto',
                overflowX: 'hidden',
                padding: '0 12px',
              }}
            >
              {body}
              {showGhost && ghostTrace}
            </div>
            <div
              style={{
                width: 1,
                flexShrink: 0,
                margin: '12px 0',
                background: T.border,
              }}
            />
            <div
              className="cpl-scroll"
              style={{
                flex: '0 0 44%',
                maxWidth: 430,
                minWidth: 320,
                overflowY: 'auto',
                overflowX: 'hidden',
                padding: '0 12px',
              }}
            >
              {patientColumn}
            </div>
          </div>
        ) : (
          <div
            ref={bodyRef}
            className="cpl-scroll"
            style={{
              flex: 1,
              minHeight: 0,
              minWidth: 0,
              overflowY: 'auto',
              overflowX: 'hidden',
              padding: '0 12px',
            }}
          >
            {body}
            {mainView && showGhost && ghostTrace}
          </div>
        )}
        {state.chatOverlay && <ChatOverlay />}
        {state.settingsOpen && <SettingsSheet />}
        {brainOpen && <BrainMonitor onClose={() => setBrainOpen(false)} />}
      </div>

      {!wide && mainView && (
        <div
          style={{
            flexShrink: 0,
            display: 'flex',
            gap: 6,
            padding: '6px 12px 0',
          }}
        >
          {[
            { key: 'queue', label: 'Queue', badge: pendingCount },
            {
              key: 'patient',
              label: focusSession
                ? (focusSession.patientName || focusSession.name).split(' ')[0]
                : 'Patient',
              recording: Boolean(focusSession?.recording),
            },
          ].map((t) => {
            const active = narrowTab === t.key;
            return (
              <span
                key={t.key}
                onClick={() => setNarrowTab(t.key)}
                role="button"
                style={{
                  flex: 1,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 5,
                  padding: '5px 0',
                  borderRadius: 8,
                  fontSize: 11,
                  fontWeight: 600,
                  cursor: 'pointer',
                  background: active ? T.surfaceCard : 'transparent',
                  color: active ? T.textPrimary : T.textSecondary,
                  border: `1px solid ${
                    active ? 'rgba(107,216,136,0.45)' : T.border
                  }`,
                }}
              >
                {t.recording && (
                  <span
                    className="cpl-dot"
                    style={{ background: T.red, width: 6, height: 6 }}
                  />
                )}
                {t.label}
                {t.badge > 0 && (
                  <span
                    style={{
                      background: 'rgba(251,191,36,0.16)',
                      color: T.amber,
                      fontSize: 9,
                      fontWeight: 700,
                      padding: '0 5px',
                      borderRadius: 7,
                    }}
                  >
                    {t.badge}
                  </span>
                )}
              </span>
            );
          })}
        </div>
      )}

      <div style={{ flexShrink: 0, padding: '6px 12px 12px' }}>
        <ChatBar />
      </div>
    </div>
  );
};

export default ExpandedPanel;
